import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Typography,
  IconButton,
  List,
  Button,
  CircularProgress,
  Tooltip,
  Divider,
  useMediaQuery,
  useTheme,
  Badge,
  Chip
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ImageModal from './ImageModal';
import VehicleAlertMapModal from './VehicleAlertMapModal';
import LocationGroup from './LocationGroup';
import CameraHighlightService from '../../utils/cameraHighlightService';
import { getEventDetails } from '../../utils/EventService';

// Priority level color mapping
const PRIORITY_COLORS = {
  'High': '#f44336',
  'Moderate': '#ff9800',
  'Low': '#4caf50'
};

const DetectionCard = ({
  title,
  icon: IconComponent,
  selected,
  onClick,
  data,
  onDelete = null,  // Making it explicitly optional with a default value
  isReportView = false,
  onLoadMore,
  isLoadingMore,
  hasMore,
  displayCount,
  totalCount,
  // New props for pause/play functionality
  isPaused = false,
  bufferedCount = 0,
  // NEW: WebSocket priority management functions
  updateEventPriority,
  getEventPriority,
  // NEW: Video streaming functions
  getVideoStreamUrlByEvent,
  getCameraByIp,
  isCameraOnline,
  // Split-view mode: card sizes to content instead of forcing 90vh
  splitView = false,
  // Close button — only shown in splitView, navigates back to home
  onClose = null,
}) => {
  const [imageSource, setImageSrc] = useState(null);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [isVehicleMapModalOpen, setIsVehicleMapModalOpen] = useState(false);
  const [selectedVehicleEvent, setSelectedVehicleEvent] = useState(null);
  const [modalArea, setModalArea] = useState('');
  const [modalLocation, setModalLocation] = useState('');
  const [expandedGroups, setExpandedGroups] = useState({});
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [processedData, setProcessedData] = useState([]);
  const [highSurveillanceCount, setHighSurveillanceCount] = useState(0);

  // VTS live closed-events cache: key = `${vehicleNumber}_${timeStamp}`, value = remark string
  // Backed by sessionStorage so it survives card remounts (view switches) but clears on full page refresh.
  // Each DetectionCard uses a unique sessionStorage key derived from its title prop.
  const vtsSessionKey = `vtsClosedCache_${title || 'default'}`;
  const [liveVtsClosedCache, setLiveVtsClosedCache] = useState(() => {
    try {
      const stored = sessionStorage.getItem(vtsSessionKey);
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });
  const handleLiveVtsEventClosed = (key, remark) => {
    setLiveVtsClosedCache(prev => {
      const next = { ...prev, [key]: remark };
      try { sessionStorage.setItem(vtsSessionKey, JSON.stringify(next)); } catch {}
      return next;
    });
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // ADD this helper function at the top of the DetectionCard component
  const isVehicleAlertCard = processedData.some(event =>
  (event.isVehicleAlert || event.alertType) && !event.isOfflineCamera
);


  const priorityStats = useMemo(() => {
    let highCount = 0;
    let moderateCount = 0;
    let lowCount = 0;
    let totalPrioritized = 0;
    let eventsWithComments = 0;

    // Debug logging
    // console.log('🔍 Calculating priority stats for', processedData.length, 'events');

    processedData.forEach((event, index) => {
      let currentSeverity = event.severity;
      let currentRemarks = event.remarks || [];
      const eventKey = event.eventId || event.id;

      // FIXED: Check for live priority updates with exact matching only
      if (!isReportView && getEventPriority && eventKey) {
        const livePriority = getEventPriority(eventKey);
        if (livePriority) {
          currentSeverity = livePriority.severity;
          currentRemarks = livePriority.remarks || [];

          // console.log(`🎯 Found live priority for event ${eventKey}: ${currentSeverity}`);
        }
      }

      // Count by priority level
      if (currentSeverity === 'High') {
        highCount++;
        totalPrioritized++;
        // console.log(`📊 High priority event found: ${eventKey}`);
      } else if (currentSeverity === 'Moderate') {
        moderateCount++;
        totalPrioritized++;
        // console.log(`📊 Moderate priority event found: ${eventKey}`);
      } else if (currentSeverity === 'Low') {
        lowCount++;
        totalPrioritized++;
        // console.log(`📊 Low priority event found: ${eventKey}`);
      }

      // Count events with comments
      if (currentRemarks && currentRemarks.length > 0) {
        eventsWithComments++;
      }
    });

    const stats = {
      high: highCount,
      moderate: moderateCount,
      low: lowCount,
      total: totalPrioritized,
      withComments: eventsWithComments,
      hasPriority: totalPrioritized > 0
    };

    // console.log('📊 Priority Stats Result:', stats);

    return stats;
  }, [processedData, getEventPriority, isReportView]);

  // Apply data to processedData state - FIXED: Remove duplicates by eventId
  useEffect(() => {
    if (data && data.length > 0) {
      // Remove duplicates based on eventId or id
      const uniqueEventsMap = new Map();
      data.forEach(event => {
        const eventKey = event.eventId || event.id;
        if (eventKey && !uniqueEventsMap.has(eventKey)) {
          uniqueEventsMap.set(eventKey, event);
        }
      });

      const uniqueEvents = Array.from(uniqueEventsMap.values());
      setProcessedData(uniqueEvents);

      // Count high surveillance events
      const highSurvEvents = uniqueEvents.filter(event =>
        event.cameraIp && CameraHighlightService.isHighSurveillance(event.cameraIp)
      ).length;
      setHighSurveillanceCount(highSurvEvents);

      //console.log(`Processed ${data.length} events, removed ${data.length - uniqueEvents.length} duplicates`);
    } else {
      setProcessedData([]);
      setHighSurveillanceCount(0);
    }
  }, [data]);

  // UPDATE the groupedEvents useMemo to handle vehicle alerts
  const groupedEvents = useMemo(() => {
    const grouped = {};

    if (processedData && processedData.length > 0) {
      processedData.forEach(event => {
        let location;

        // For vehicle alerts, group by transporter
        if (event.isVehicleAlert || event.alertType) {
          location = event.vehicleTransporter || "Unknown Transporter";
        } else {
          // For camera events, group by location
          location = event.location || "Unknown";
        }

        if (!grouped[location]) {
          grouped[location] = {
            events: [],
            hasSDLocation: false,
            latestTimestamp: 0,
            hasHighSurveillance: false,
            eventCount: 0,
            isVehicleGroup: event.isVehicleAlert || event.alertType
          };
        }

        // Add parent location name to the event display
        const eventCount = grouped[location].eventCount + 1;
        let eventWithUpdatedName;

        if (event.isVehicleAlert || event.alertType) {
          // For vehicle alerts, use vehicle number and alert type
          eventWithUpdatedName = {
            ...event,
            displayName: `${event.vehicleNumber} - ${event.alertType?.toUpperCase() || 'ALERT'}`
          };
        } else {
          // UPDATED: For camera events, show specific detection type (PD or ID)
          const eventTypeDisplay = event.detectionType === 'PD' ? 'Person' :
            event.detectionType === 'ID' ? 'Intrusion' :
              event.type === 'PD' ? 'Person' :
                event.type === 'ID' ? 'Intrusion' :
                  event.detectionType || 'Event';
          eventWithUpdatedName = {
            ...event,
            displayName: `${eventTypeDisplay} ${eventCount} - ${location}`
          };
        }

        grouped[location].events.push(eventWithUpdatedName);
        grouped[location].eventCount = eventCount;

        // Check if this is an SD Location (only for camera events)
        if (!event.isVehicleAlert && event.isSDLocation) {
          grouped[location].hasSDLocation = true;
        }

        // Check for high surveillance (only for camera events)
        if (!event.isVehicleAlert && event.cameraIp && CameraHighlightService.isHighSurveillance(event.cameraIp)) {
          grouped[location].hasHighSurveillance = true;
        }

        // Or check if the location name includes "Kusunda" (only for camera events)
        if (!event.isVehicleAlert && location.toLowerCase().includes("kusunda")) {
          grouped[location].hasHighSurveillance = true;
        }

        // Find the latest timestamp for this location's events
        if (event.id || event.eventId) {
          const eventIdToUse = event.eventId || event.id;
          let timestamp;

          if (event.isVehicleAlert && event.timestamp) {
            // For vehicle alerts, use the IST timestamp
            timestamp = event.timestamp;
          } else {
            // For camera events, parse from event ID
            timestamp = parseInt(eventIdToUse.split("_")[0], 10);
          }

          if (timestamp > grouped[location].latestTimestamp) {
            grouped[location].latestTimestamp = timestamp;
          }
        }
      });

      // Sort events within each location by timestamp
      Object.keys(grouped).forEach(location => {
        grouped[location].events.sort((a, b) => {
          let timestampA, timestampB;

          if (a.isVehicleAlert && a.timestamp) {
            timestampA = a.timestamp;
          } else {
            const eventIdA = a.eventId || a.id;
            timestampA = eventIdA ? parseInt(eventIdA.split("_")[0], 10) : 0;
          }

          if (b.isVehicleAlert && b.timestamp) {
            timestampB = b.timestamp;
          } else {
            const eventIdB = b.eventId || b.id;
            timestampB = eventIdB ? parseInt(eventIdB.split("_")[0], 10) : 0;
          }

          return timestampB - timestampA; // Newest first
        });
      });
    }

    return grouped;
  }, [processedData]);


  // Sort locations by timestamp only (newest first)
  const sortedLocations = useMemo(() => {
    return Object.entries(groupedEvents).sort((a, b) => {
      const [, dataA] = a;
      const [, dataB] = b;

      // Sort by timestamp (newest first)
      return dataB.latestTimestamp - dataA.latestTimestamp;
    });
  }, [groupedEvents]);

  const toggleGroup = (location) => {
    setExpandedGroups(prev => ({
      ...prev,
      [location]: !prev[location]
    }));
  };

  // UPDATED handleEventClick function to handle vehicle alerts with map modal
  const handleEventClick = async (e, event) => {
    e.stopPropagation();
    e.currentTarget.style.backgroundColor = '#1ABC9C55';

    // For vehicle alerts, open map modal instead of image modal
    if (event.isVehicleAlert || event.alertType) {
      console.log('Vehicle Alert Details:', {
        vehicleNumber: event.vehicleNumber,
        alertType: event.alertType,
        transporter: event.vehicleTransporter,
        coordinates: event.coordinates,
        alertSubType: event.alertSubType,
        timestamp: event.readableTime,
        geofence: event.allocatedGeofence
      });

      setSelectedVehicleEvent(event);
      setIsVehicleMapModalOpen(true);

      setTimeout(() => {
        if (e.currentTarget && e.currentTarget.style) {
          e.currentTarget.style.backgroundColor = '';
        }
      }, 1000);

      return;
    }

    // UPDATED: Enhanced logging for separate PD and ID events
    // console.log('Camera Event Details:', {
    //   eventType: event.detectionType || event.type,
    //   eventId: event.eventId || event.id,
    //   location: event.location,
    //   area: event.area,
    //   displayName: event.displayName,
    //   isPD: event.detectionType === 'PD' || event.type === 'PD',
    //   isID: event.detectionType === 'ID' || event.type === 'ID'
    // });

    try {
      // Check for existing priority in live dashboard (for non-report views)
      let modalEvent = { ...event };

      if (!isReportView && getEventPriority) {
        const eventKey = event.eventId || event.id;
        const existingPriority = getEventPriority(eventKey);
        if (existingPriority) {
          modalEvent.severity = existingPriority.severity;
          modalEvent.remarks = existingPriority.remarks || [];
          modalEvent.priorityUpdated = true;
          modalEvent.priorityUpdatedAt = existingPriority.updatedAt;
        }
      }

      // UPDATED: Store basic event information with specific detection type
      modalEvent = {
        ...modalEvent,
        severity: modalEvent.severity || event.severity || '',
        remarks: modalEvent.remarks || event.remarks || [],
        // UPDATED: Specific detection type handling
        detectionSubType: event.detectionType === 'PD' ? 'Person Detection' :
          event.detectionType === 'ID' ? 'Intrusion Detection' :
            event.type === 'PD' ? 'Person Detection' :
              event.type === 'ID' ? 'Intrusion Detection' :
                'Detection Event'
      };

      setSelectedEvent(modalEvent);
      setModalLocation(event.location);
      setModalArea(event.area);

      // Get the database ID from event (only for report views)
      if (isReportView) {
        const eventKey = event.eventId || event.id;
        if (!eventKey) {
          console.warn('No eventId found in event data', event);
        } else {
          modalEvent.eventId = eventKey;
        }

        // Get base URL for API calls (only for report views)
        if (!event.httpsUrl) {
          console.warn('No httpsUrl found in event data', event);
        } else {
          modalEvent.httpsUrl = event.httpsUrl;
        }

        // Only fetch details if we have both eventId and httpsUrl
        if (eventKey && event.httpsUrl) {
          try {
            const details = await getEventDetails(event.httpsUrl, eventKey);
            if (details && details.data) {
              // Merge details into event
              modalEvent.severity = details.data.severity || modalEvent.severity;
              modalEvent.remarks = details.data.remarks || modalEvent.remarks;

              // Update the selectedEvent with the enhanced data
              setSelectedEvent(modalEvent);
            }
          } catch (detailsError) {
            console.warn('Could not fetch event details:', detailsError);
            // Continue with basic event data
          }
        }
      }

      // Fetch the image based on the event type
      try {
        let imageUrl;

        if (isReportView && event.imageUrl) {
          // For report view, directly use the stored image URL if available
          imageUrl = event.imageUrl;
        } else if (event.httpsUrl && (event.id || event.eventId)) {
          // For live view, fetch the image from the API
          const eventKey = event.eventId || event.id;
          const response = await fetch(`${event.httpsUrl}/api/va/event/?id=${eventKey}`);

          if (response.ok) {
            const blob = await response.blob();
            imageUrl = URL.createObjectURL(blob);
          } else {
            throw new Error('Could not fetch event image');
          }
        } else {
          throw new Error('Missing URL or event ID for image fetch');
        }

        setImageSrc(imageUrl);
        setIsImageModalOpen(true);
      } catch (error) {
        console.error('Image fetch error:', error);
        alert('Image fetch failed: ' + error.message);
      }
    } catch (error) {
      console.error('Error preparing event for modal:', error);
      alert('Failed to prepare event data: ' + error.message);
    }
  };

  // Add this handler function for closing the vehicle map modal
  const handleVehicleMapModalClose = () => {
    setIsVehicleMapModalOpen(false);
    setSelectedVehicleEvent(null);
  };

  // FIXED: Handler for event updates from the modal
  // FIXED: Handler for event updates from the modal with exact matching
  const handleEventUpdated = (updatedEvent) => {
    console.log('HandleEventUpdated called with:', {
      eventId: updatedEvent.eventId || updatedEvent.id,
      severity: updatedEvent.severity,
      remarksCount: updatedEvent.remarks?.length || 0
    });

    // Update the selected event with new data
    setSelectedEvent(updatedEvent);

    // FIXED: Update only the exact matching event in processedData
    setProcessedData(prevData => {
      if (!Array.isArray(prevData)) {
        console.warn('⚠️ ProcessedData is not an array');
        return [];
      }

      const updatedKey = updatedEvent.eventId || updatedEvent.id;
      if (!updatedKey) {
        console.error('❌ No eventId found in updatedEvent');
        return prevData;
      }

      let updatedCount = 0;
      const updatedData = prevData.map(item => {
        const itemKey = item.eventId || item.id;

        // FIXED: Exact matching only - no fuzzy matching
        if (itemKey && itemKey === updatedKey) {
          console.log(`✅ Exact match found - updating event ${itemKey}`);
          updatedCount++;

          return {
            ...item,
            severity: updatedEvent.severity,
            remarks: updatedEvent.remarks || [],
            priorityUpdated: updatedEvent.priorityUpdated || true,
            priorityUpdatedAt: updatedEvent.priorityUpdatedAt || Date.now(),
            isLivePrioritized: true
          };
        }

        return item; // Return unchanged for non-matching events
      });

      console.log(`📊 ProcessedData update complete:`, {
        totalEvents: prevData.length,
        eventsUpdated: updatedCount,
        targetEventId: updatedKey,
        foundMatch: updatedCount > 0
      });

      if (updatedCount === 0) {
        console.warn(`⚠️ No matching event found for eventId: ${updatedKey}`);
        console.log('Available event IDs:', prevData.map(item => item.eventId || item.id).slice(0, 10));
      }

      return updatedData;
    });
  };

  const handleCloseModal = () => {
    setIsImageModalOpen(false);
    if (imageSource) {
      URL.revokeObjectURL(imageSource);
      setImageSrc(null);
    }
  };

  // Create the Load More button component for header
  const LoadMoreButton = () => (
    <Button
      variant="outlined"
      startIcon={isLoadingMore ? <CircularProgress size={16} color="inherit" /> : <MoreHorizIcon />}
      endIcon={<KeyboardArrowDownIcon />}
      onClick={(e) => {
        e.stopPropagation();
        onLoadMore();
      }}
      disabled={isLoadingMore}
      size="small"
      sx={{
        color: '#1ABC9C',
        borderColor: '#1ABC9C',
        fontSize: '0.7rem',
        padding: '3px 8px',
        minWidth: 'auto',
        height: '24px',
        '&:hover': {
          backgroundColor: 'rgba(26, 188, 156, 0.08)',
          borderColor: '#1ABC9C'
        }
      }}
    >
      {isLoadingMore ? 'Loading...' : 'Load More'}
    </Button>
  );

  // Resolve accent color used for the premium gradient header/glow.
  const getAccentColor = () => {
    if (priorityStats.high > 0) return "#f44336";
    if (priorityStats.moderate > 0) return "#ff9800";
    if (priorityStats.low > 0) return "#4caf50";
    if (isVehicleAlertCard) return "#FF6B35";
    if (isPaused) return "#f44336";
    return "#1ABC9C";
  };

  // UPDATE the card styling to differentiate vehicle alerts
  const getCardBorderColor = () => {
    if (priorityStats.high > 0) return "1.5px solid rgba(244, 67, 54, 0.55)";
    if (priorityStats.moderate > 0) return "1.5px solid rgba(255, 152, 0, 0.55)";
    if (priorityStats.low > 0) return "1.5px solid rgba(76, 175, 80, 0.55)";
    if (isVehicleAlertCard) return "1.5px solid rgba(255, 107, 53, 0.5)";
    if (isPaused) return "1px solid rgba(244, 67, 54, 0.3)";
    return "1px solid rgba(255,255,255,0.08)";
  };

  const getCardBackgroundColor = () => {
    // Subtle premium gradient — accent tint at top, deep slate base.
    const accent = getAccentColor();
    return `linear-gradient(180deg, ${accent}14 0%, rgba(22,32,45,0.96) 28%, rgba(20,28,40,0.98) 100%)`;
  };

  // Create priority chips for header
  const PriorityChips = () => {
    if (!priorityStats.hasPriority) return null;

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mr: 1 }}>
        {priorityStats.high > 0 && (
          <Chip
            label={`${priorityStats.high} High`}
            size="small"
            sx={{
              bgcolor: PRIORITY_COLORS.High,
              color: 'white',
              fontSize: '0.7rem',
              height: 20,
              fontWeight: 'bold'
            }}
          />
        )}
        {priorityStats.moderate > 0 && (
          <Chip
            label={`${priorityStats.moderate} Moderate`}
            size="small"
            sx={{
              bgcolor: PRIORITY_COLORS.Moderate,
              color: 'white',
              fontSize: '0.7rem',
              height: 20,
              fontWeight: 'bold'
            }}
          />
        )}
        {priorityStats.low > 0 && (
          <Chip
            label={`${priorityStats.low} Low`}
            size="small"
            sx={{
              bgcolor: PRIORITY_COLORS.Low,
              color: 'white',
              fontSize: '0.7rem',
              height: 20,
              fontWeight: 'bold'
            }}
          />
        )}
        {priorityStats.withComments > 0 && (
          <Chip
            label={`${priorityStats.withComments} comments`}
            size="small"
            sx={{
              bgcolor: 'rgba(255,255,255,0.2)',
              color: 'white',
              fontSize: '0.7rem',
              height: 20
            }}
          />
        )}
      </Box>
    );
  };

  return (
    <>
      <Card
        onClick={onClick}
        elevation={0}
        sx={{
          position: 'relative',
          overflow: 'hidden',
          height: (selected && !splitView) ? "90vh" : splitView ? "auto" : "43vh",
          margin: "0px",
          background: getCardBackgroundColor(),
          color: "white",
          borderRadius: splitView ? "14px" : "10px",
          border: getCardBorderColor(),
          transition: "all 0.3s ease",
          boxShadow: priorityStats.high > 0 ? "0 10px 32px rgba(244, 67, 54, 0.28), inset 0 1px 0 rgba(255,255,255,0.04)" :
            priorityStats.moderate > 0 ? "0 10px 32px rgba(255, 152, 0, 0.22), inset 0 1px 0 rgba(255,255,255,0.04)" :
              priorityStats.low > 0 ? "0 10px 32px rgba(76, 175, 80, 0.22), inset 0 1px 0 rgba(255,255,255,0.04)" :
                splitView ? "0 10px 30px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.05)" : "none",
          display: "flex",
          flexDirection: "column",
          cursor: "pointer",
          // Top accent ribbon — gives the premium "hero" feel in split view.
          '&::before': splitView ? {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '3px',
            background: `linear-gradient(90deg, ${getAccentColor()} 0%, ${getAccentColor()}99 50%, transparent 100%)`,
            boxShadow: `0 0 12px ${getAccentColor()}aa`,
            zIndex: 2,
          } : undefined,
          // Subtle radial glow behind the header.
          '&::after': splitView ? {
            content: '""',
            position: 'absolute',
            top: -40,
            left: -40,
            width: 280,
            height: 180,
            background: `radial-gradient(ellipse at top left, ${getAccentColor()}22 0%, transparent 70%)`,
            pointerEvents: 'none',
            zIndex: 0,
          } : undefined,
        }}
      >
        <CardHeader
          avatar={
            bufferedCount > 0 ? (
              <Badge
                badgeContent={bufferedCount}
                color="error"
                max={999}
                overlap="circular"
              >
                <IconComponent sx={{
                  color: priorityStats.high > 0 ? "#f44336" :
                    priorityStats.moderate > 0 ? "#ff9800" :
                      priorityStats.low > 0 ? "#4caf50" :
                        isVehicleAlertCard ? "#FF6B35" : // Orange for vehicle alerts
                          isPaused ? "#f44336" : "#1ABC9C"
                }} />
              </Badge>
            ) : (
              <IconComponent sx={{
                color: priorityStats.high > 0 ? "#f44336" :
                  priorityStats.moderate > 0 ? "#ff9800" :
                    priorityStats.low > 0 ? "#4caf50" :
                      isVehicleAlertCard ? "#FF6B35" : // Orange for vehicle alerts
                        isPaused ? "#f44336" : "#1ABC9C"
              }} />
            )
          }
          titleTypographyProps={{ variant: "h6" }}
          action={
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <PriorityChips />
              {isReportView && hasMore ? (
                <LoadMoreButton />
              ) : onDelete && !isReportView ? (
                <Tooltip title="Clear">
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      if (typeof onDelete === 'function') {
                        onDelete();
                        console.log('Clearing events for:', title);
                      }
                    }}
                    size="small"
                    sx={{
                      color: "#FF6B6B",
                      backgroundColor: "rgba(255,255,255,0.1)",
                      mr: 1,
                      "&:hover": { backgroundColor: "rgba(255,255,255,0.2)" },
                    }}
                  >
                    <DeleteIcon sx={{ fontSize: '1.25rem' }} />
                  </IconButton>
                </Tooltip>
              ) : null}
              {splitView && onClose && (
                <Tooltip title="Back to Home">
                  <IconButton
                    onClick={(e) => { e.stopPropagation(); onClose(); }}
                    size="small"
                    sx={{
                      color: "#aaa",
                      backgroundColor: "rgba(255,255,255,0.08)",
                      mr: 0.5,
                      "&:hover": { backgroundColor: "rgba(255,255,255,0.18)", color: "#fff" },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: '1.1rem' }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          }
          title={
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography
                sx={{
                  color: "white",
                  fontWeight: splitView ? 700 : 500,
                  fontSize: splitView ? '1.15rem' : '1rem',
                  letterSpacing: splitView ? '0.02em' : 'normal',
                  textAlign: "left",
                  textShadow: splitView ? `0 0 18px ${getAccentColor()}40` : 'none',
                }}
              >
                {title}
                {/* FIXED: Only show Vehicle Alerts for actual vehicle alerts, not offline cameras */}
                {isVehicleAlertCard && (
                  <Typography component="span" sx={{ color: "#FF6B35", ml: 1, fontSize: "0.85rem", fontWeight: 500, opacity: 0.9 }}>
                    (Vehicle Alerts)
                  </Typography>
                )}
                {isReportView && (
                  <Typography component="span" sx={{ color: "#1ABC9C", ml: 1, fontSize: "0.85rem", fontWeight: 500, opacity: 0.9 }}>
                    (Report)
                  </Typography>
                )}
                {isPaused && (
                  <Typography component="span" sx={{ color: "#f44336", ml: 1, fontSize: "0.85rem", fontWeight: 500, opacity: 0.9 }}>
                    (Paused)
                  </Typography>
                )}
              </Typography>
            </Box>
          }
          sx={{
            padding: splitView ? "16px 18px 10px" : "12px 16px 8px",
            position: 'relative',
            zIndex: 1,
            '& .MuiCardHeader-content': {
              overflow: 'visible'
            },
            '& .MuiCardHeader-avatar': {
              marginRight: splitView ? '14px' : '12px',
              '& .MuiSvgIcon-root': {
                fontSize: splitView ? '1.6rem' : '1.4rem',
                filter: splitView ? `drop-shadow(0 0 8px ${getAccentColor()}66)` : 'none',
              }
            }
          }}
        />

        {/* Count display below title */}
        <Box sx={{ px: splitView ? 2.25 : 2, pb: 1, position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {splitView && (
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  backgroundColor: getAccentColor(),
                  boxShadow: `0 0 8px ${getAccentColor()}`,
                  animation: 'liveCountDot 1.6s ease-in-out infinite',
                  '@keyframes liveCountDot': {
                    '0%,100%': { opacity: 1 },
                    '50%': { opacity: 0.35 },
                  },
                }}
              />
            )}
            <Typography
              variant="body2"
              sx={{
                color: bufferedCount > 0 && isPaused ? '#f44336' : getAccentColor(),
                fontSize: splitView ? '0.78rem' : '0.82rem',
                fontWeight: splitView ? 600 : 400,
                letterSpacing: splitView ? '0.04em' : 'normal',
                textTransform: splitView ? 'uppercase' : 'none',
              }}
            >
              Showing {displayCount || data.length} of {totalCount || data.length} events
              {bufferedCount > 0 && isPaused && ` (${bufferedCount} buffered)`}
              {(displayCount >= totalCount) && totalCount > 0 && " (All data loaded)"}
            </Typography>
          </Box>
          <Divider sx={{ bgcolor: splitView ? `${getAccentColor()}30` : '#2C3E50', my: 1, height: splitView ? '1.5px' : '1px' }} />
        </Box>

        <CardContent
          sx={{
            flexGrow: 1,
            padding: splitView ? "0 18px 18px 18px" : "0 16px 16px 16px",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            position: 'relative',
            zIndex: 1,
          }}
        >
          <Box
            sx={{
              flexGrow: 1,
              height: (selected && !splitView) ? "calc(75vh - 16px)" : splitView ? "auto" : "calc(30vh - 16px)",
              maxHeight: (selected && !splitView) ? "calc(80vh - 80px)" : splitView ? "45vh" : "calc(35vh - 24px)",
              overflowY: "auto",
              paddingRight: "8px",
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: '#17212b',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#2a3f55',
                borderRadius: '4px',
              },
            }}
          >
            {processedData.length > 0 ? (
              sortedLocations.length > 0 ? (
                <List sx={{ padding: 0 }}>
                  {sortedLocations
                    .slice(0, selected ? 100 : sortedLocations.length)
                    .map(([location, locationData]) => (
                      <LocationGroup
                        key={location}
                        location={location}
                        locationData={locationData}
                        expanded={expandedGroups[location] || false}
                        onToggle={() => toggleGroup(location)}
                        onEventClick={handleEventClick}
                        getEventPriority={getEventPriority}
                        isReportView={isReportView}
                        liveVtsClosedCache={liveVtsClosedCache}
                        onLiveVtsEventClosed={handleLiveVtsEventClosed}
                      />
                    ))}
                </List>
              ) : (
                processedData.map((event, index) => (
                  <Box
                    key={event.eventId || event.id || index} // FIXED: Use eventId as primary key
                    onClick={(e) => handleEventClick(e, event)}
                    sx={{
                      p: 1,
                      mb: 1,
                      borderLeft: `4px solid ${event.status === 'alert' ? '#FF6B6B' : '#1ABC9C'}`,
                      backgroundColor: '#263340',
                      borderRadius: '0 4px 4px 0',
                      '&:hover': {
                        backgroundColor: '#2C3E50'
                      }
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ color: 'white' }}>
                      {event.displayName || event.location || 'Unknown Event'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#637592' }}>
                      {event.timestamp ? new Date(event.timestamp).toLocaleString() : 'No timestamp'}
                    </Typography>
                  </Box>
                ))
              )
            ) : (
              <Typography variant="body2" sx={{ color: "#637592", textAlign: "center", mt: 2 }}>
                No events to display
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Pass the WebSocket priority functions to ImageModal */}
      <ImageModal
        isOpen={isImageModalOpen}
        onClose={handleCloseModal}
        imageSource={imageSource}
        area={modalArea}
        location={modalLocation}
        event={selectedEvent}
        onEventUpdated={handleEventUpdated}
        updateEventPriority={updateEventPriority}
        isReportView={isReportView}
        // NEW: Video streaming props
        getVideoStreamUrlByEvent={getVideoStreamUrlByEvent}
        getCameraByIp={getCameraByIp}
        isCameraOnline={isCameraOnline}
      />

      {/* Vehicle Alert Map Modal */}
      <VehicleAlertMapModal
        open={isVehicleMapModalOpen}
        onClose={handleVehicleMapModalClose}
        event={selectedVehicleEvent}
        liveClosedCache={liveVtsClosedCache}
        onLiveEventClosed={handleLiveVtsEventClosed}
      />
    </>
  );
};

export default DetectionCard;